import React from 'react';
import env from '../../env';

/**
 * ContactPageSchema Component
 * 
 * Implements Schema.org ContactPage structured data for Contact pages.
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/ContactPage
 * 
 * @param {Object} props
 * @param {string} props.name - Page title
 * @param {string} props.description - Page description
 * @param {string} props.url - Page URL (relative or absolute)
 * @param {Object} props.breadcrumb - Breadcrumb data (optional)
 * @param {string} props.image - Page image URL (optional)
 * 
 * @example
 * <ContactPageSchema
 *   name="Contact Us | Scunci"
 *   description="Get in touch with Scunci"
 *   url="/contact"
 *   breadcrumb={breadcrumbData}
 * />
 */
export function ContactPageSchema({ 
  name, 
  description, 
  url, 
  breadcrumb,
  image 
}) {
  React.useEffect(() => {
    // Validate required data
    if (!name || !url) {
      return;
    }

    // Remove any existing contact page schema script
    const existingScript = document.querySelector('script[data-contact-page-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Build absolute URL
    let absoluteUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      const normalizedUrl = url.startsWith('/') ? url : '/' + url;
      absoluteUrl = env.baseOrigin + normalizedUrl;
    }

    // Build absolute image URL if provided
    let absoluteImageUrl = null;
    if (image) {
      if (image.startsWith('http://') || image.startsWith('https://')) {
        absoluteImageUrl = image;
      } else {
        const normalizedImage = image.startsWith('/') ? image : '/' + image;
        absoluteImageUrl = env.baseOrigin + normalizedImage;
      }
    }

    // Build ContactPage schema according to Schema.org specification
    const contactPageSchema = {
      "@context": "https://schema.org/",
      "@type": "ContactPage",
      "@id": absoluteUrl,
      "name": name,
      "url": absoluteUrl
    };

    // Add description if provided
    if (description) {
      contactPageSchema.description = description;
    }

    // Add image if provided
    if (absoluteImageUrl) {
      contactPageSchema.image = absoluteImageUrl;
    }

    // Add breadcrumb reference if provided
    if (breadcrumb && breadcrumb.items && breadcrumb.items.length > 0) {
      contactPageSchema.breadcrumb = {
        "@id": absoluteUrl + "#breadcrumb"
      };
    }

    // Create and inject JSON-LD script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-contact-page-schema', 'true');
    script.textContent = JSON.stringify(contactPageSchema, null, 0);
    document.head.appendChild(script);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const scriptToRemove = document.querySelector('script[data-contact-page-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, url, breadcrumb, image]); // Re-run if any prop changes

  // Return null since we're using direct DOM manipulation
  return null;
}
